<script lang="ts">
	import XMLViewer from '../XMLViewer.svelte';
	import { FileText, Music, Search, CheckCircle, Shield, Rocket, Palette } from 'lucide-svelte';
	import { useReleaseContext } from '../../context/release';

	// Phase 6: Svelte 5 callback props! 🚀
	let { onpreview }: { onpreview?: () => void } = $props();

	// Phase 3: Use cleaner context interface
	const { releaseConfigState, releaseRuntimeState } = useReleaseContext();

	let isApple = $derived(($releaseConfigState.outputFormat ?? 'ddex-ern') === 'apple-itunes');

	let downloadFilename = $derived(
		isApple
			? 'metadata'
			: $releaseConfigState.releaseType === 'SingleResourceRelease'
				? ($releaseConfigState.releaseId?.proprietaryId?.value || 'release')
				: ($releaseConfigState.releaseId?.ICPN || 'release')
	);

	let formatLabel = $derived(isApple ? 'Apple iTunes Package' : 'DDEX ERN');

	function handlePreview() {
		onpreview?.();
	}
</script>

<div class="space-y-8">

	<!-- XML Content -->
	{#if $releaseRuntimeState.generatedXML}
		<div class="card card-hover animate-slide-up">
			<div class="card-body">
				<div class="flex items-start gap-4 mb-6">
					<div class="avatar avatar-md bg-purple-600 text-white">
						<Search size={24} />
					</div>
					<div>
						<h2 class="text-2xl font-bold text-gray-900 mb-1">Generated XML Content</h2>
						<p class="text-gray-600">{formatLabel} file ready for validation</p>
					</div>
				</div>
				<XMLViewer xml={$releaseRuntimeState.generatedXML} releaseId={downloadFilename} />
			</div>
		</div>
	{/if}

	<!-- Validation Results -->
	{#if $releaseRuntimeState.validationMessage}
		<div class="card card-hover animate-slide-up">
			<div class="card-body">
				<div class="flex items-start gap-4 mb-6">
					<div class="avatar avatar-md bg-green-600 text-white">
						<Shield size={24} />
					</div>
					<div>
						<h2 class="text-2xl font-bold text-gray-900 mb-1">Validation Complete</h2>
						<p class="text-gray-600">Your {formatLabel} file meets all requirements</p>
					</div>
				</div>
				
				<!-- Validation Stats -->
				<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
					{#if !isApple}
					<div class="card">
						<div class="card-body text-center">
							<div class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">ERN Version</div>
							<div class="text-2xl font-bold text-blue-600">{$releaseConfigState.ernVersion}</div>
						</div>
					</div>
					<div class="card">
						<div class="card-body text-center">
							<div class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">AVS Version</div>
							<div class="text-2xl font-bold text-purple-600">{$releaseConfigState.avsVersion}</div>
						</div>
					</div>
					{/if}
					<div class="card">
						<div class="card-body text-center">
							<div class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">{isApple ? 'Format' : 'Release Profile'}</div>
							<div class="text-2xl font-bold text-green-600">{isApple ? 'Apple iTunes' : $releaseConfigState.releaseProfile}</div>
						</div>
					</div>
					<div class="card">
						<div class="card-body text-center">
							<div class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Release Type</div>
							<div class="text-2xl font-bold text-amber-600">{$releaseConfigState.releaseType}</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	{/if}
	
	<!-- Next Step -->
	<div class="card card-hover animate-scale-in">
		<div class="card-body text-center">
			{#if $releaseConfigState.generateAiAssets}
				<div class="avatar avatar-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white mx-auto mb-6">
					<Palette size={32} />
				</div>
				<h2 class="text-3xl font-bold text-gray-900 mb-3">Ready for Asset Generation</h2>
				<p class="text-lg text-gray-600 mb-8">AI-powered assets will be generated for your release</p>
			{:else}
				<div class="avatar avatar-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white mx-auto mb-6">
					<Rocket size={32} />
				</div>
				<h2 class="text-3xl font-bold text-gray-900 mb-3">Ready for Delivery</h2>
				<p class="text-lg text-gray-600 mb-8">Your validated release can now be delivered to the platform</p>
			{/if}
			<button class="btn btn-primary btn-lg" onclick={handlePreview}>
				{#if $releaseConfigState.generateAiAssets}
					<Palette size={20} />
				{:else}
					<Rocket size={20} />
				{/if}
				Preview Release
			</button>
		</div>
	</div>
</div>